import { Avatar } from "@resource/atlas/avatar/Avatar";
import Badge from "@resource/atlas/badge/Badge";
import { Link } from "@resource/atlas/link/Link";
import { DefaultTemplateBadge } from "client/components/display/badges/DefaultTemplateBadge";
import { InterviewPlanView } from "client/components/interview-plan/InterviewPlanView";
import GuideBanner from "components/Guide/Banner";
import ModalSelectWithPreviewContent from "components/ModalSelectWithPreview/ModalSelectWithPreviewContent";
import { UpdatedDate } from "components/UpdatedDate";
import { gql } from "generated/graphql-codegen";
import {
  OrderByArg,
  ZeusGuideTemplatesForSelectQuery,
} from "generated/graphql-codegen/graphql";
import { formatEntity } from "shared/constants/entities";
import useQuery from "utils/useQuery";

type GuideTemplate = NonNullable<
  ZeusGuideTemplatesForSelectQuery["currentOrganization"]
>["guideTemplates"][number];

const HIRING_PLANS = gql(`
query ZeusGuideTemplatesForSelect(
    $orderBy: [OrganizationGuideTemplatesOrderByInput!]
  ) {
    currentOrganization {
      id
      defaultAvatarImageUrl
      companyLogoUrl
      name
      theme {
        primaryColor
        secondaryColor
      }
      guideTemplates(orderBy: $orderBy, where: { status: ACTIVE }) {
        id
        name
        isGlobal
        departments {
          id
          name
        }
        interviewPlan {
          ...InterviewPlan
        }
        updatedAt
        guideBanner
      }
    }
  }
`);

interface ZeusGuideTemplateSelectionViewProps {
  selectedGuideTemplate: GuideTemplate | null;
  setSelectedGuideTemplate: (arg: GuideTemplate | null) => void;
  currentGuideTemplateId?: string;
}

function ZeusGuideTemplateSelectionView({
  selectedGuideTemplate,
  setSelectedGuideTemplate,
  currentGuideTemplateId,
}: ZeusGuideTemplateSelectionViewProps) {
  const { data, loading } = useQuery(HIRING_PLANS, {
    variables: {
      orderBy: [
        {
          name: "asc" as OrderByArg,
        },
      ],
    },
  });

  return (
    <ModalSelectWithPreviewContent
      idResolver={(guideTemplate) => guideTemplate.id}
      isLoading={loading}
      badgeResolver={(guideTemplate) => (
        <>
          <DefaultTemplateBadge
            className="ml-1"
            size="small"
            guideTemplate={guideTemplate}
          />
          {currentGuideTemplateId &&
            guideTemplate.id === currentGuideTemplateId && (
              <Badge size="small" variant="success" className="ml-1">
                Current
              </Badge>
            )}
        </>
      )}
      menuItemTitleResolver={(guideTemplate) => guideTemplate.name}
      previewItems={data?.currentOrganization?.guideTemplates ?? []}
      renderPreviewContent={(guideTemplate) => (
        <div className="text-body-md x-4 lg:px-8 mx-auto h-full lg:min-h-[40vh] space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex gap-2 items-center">
                <p className="text-body-lg-heavy truncate">
                  {guideTemplate.name}
                </p>
                <DefaultTemplateBadge
                  size="small"
                  guideTemplate={guideTemplate}
                />
              </div>
              <Link
                className="pl-[0.125rem]"
                href={`/templates/guides/${guideTemplate.id}`}
                target="_blank"
              >
                Edit
              </Link>
            </div>
            {guideTemplate.updatedAt && (
              <UpdatedDate date={new Date(guideTemplate.updatedAt)} />
            )}
          </div>
          {guideTemplate.guideBanner && (
            <div className="flex flex-col gap-4 !mb-[72px] relative">
              <h4 className="text-h4 text-dark">
                {formatEntity("cover image", { capitalize: true })}
              </h4>
              <GuideBanner
                bannerUrl={guideTemplate.guideBanner}
                color={data?.currentOrganization?.theme?.primaryColor}
              />
              <div className="absolute -bottom-12 left-8">
                <Avatar
                  size="3xl"
                  image={data?.currentOrganization?.defaultAvatarImageUrl}
                  name={data?.currentOrganization?.name}
                />
              </div>
            </div>
          )}
          {guideTemplate.interviewPlan && (
            <section className="space-y-4">
              <h4 className="text-h4">
                {formatEntity("interview plan", { capitalize: true })}
              </h4>
              <InterviewPlanView
                interviewPlan={guideTemplate.interviewPlan}
                isLoading={loading}
              />
            </section>
          )}
        </div>
      )}
      selectedItem={selectedGuideTemplate}
      setSelectedItem={setSelectedGuideTemplate}
    />
  );
}

export default ZeusGuideTemplateSelectionView;
